import React, { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import { downloadFile, uploadFile } from "@utils/utilsApi";
import { ActionsType } from "src/interfaces/Common";
import { KeyURL, MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import useSWRMutation from "swr/mutation";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import ButtonBackPage from "@components/ButtonBackPage";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const Project: React.FC = () => {
  const [idProjectDeleted, setidProjectDeleted] = useState<string>();
  const [idProjectRequestActivate, setIdProjectRequestActivate] =
    useState<string>();
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalRequestActivate, setOpenModalRequestActivate] =
    useState(false);
  const { id } = useParams();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["projects"]}`;

  /*########### ROLE PERMISSION ###########*/

  /*Permission to indetify PM*/
  const isPM = user?.permissions.find(
    (permission: any) =>
      permission === "usermgmt.budget.project.mark.as.pending.activation",
  );

  /*#######################################*/

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      `${ServicesURL.getProjects}?corporateId=${id}`,
      PagesID["projects"],
      filters[filterId],
    );

  const { t } = useTranslation(["projects-page"]);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();

  /*Function to triggere the delete*/
  const { trigger: triggerDeleteProject } = useSWRMutation(
    `${ServicesURL.postProjects}/${idProjectDeleted}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  /*Function to handle the delete of a project*/
  const handleDelete = React.useCallback(async () => {
    if (idProjectDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteProject();
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idProjectDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setidProjectDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idProjectDeleted, mutate, triggerDeleteProject]);

  /*Function to triggere the action to request the activation*/
  const { trigger: triggerRequestActivate } = useSWRMutation(
    `${ServicesURL.postProjects}/${idProjectRequestActivate}/actions/mark-as-pending-activation?corporateId=${id}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.POST);
    },
  );

  /*Function to handle action to request the activation of a project*/
  const handleRequestActivate = React.useCallback(async () => {
    if (idProjectRequestActivate) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerRequestActivate();
        mutate && mutate({ ...dataTable });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdProjectRequestActivate(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [dataTable, idProjectRequestActivate, mutate, triggerRequestActivate]);

  useEffect(() => {
    if (!idProjectDeleted) return;
  }, [idProjectDeleted]);

  const dataFormat = useMemo(() => {
    if (!dataTable?.output) return [];

    return dataTable.output.map((el: any) => {
      if (el.cost_center) {
        el.cost_center.label = el.cost_center.sap_code;
      }

      if (el.project_manager) {
        el.project_manager.label =
          el.project_manager.last_name + " " + el.project_manager.first_name;
      }

      if (Array.isArray(el.customers)) {
        el.customers = el.customers.map((cust: any) => ({
          ...cust,
          label: cust.business_name,
        }));
      }

      if (el.financing_plan) {
        el.financing_plan.label = el.financing_plan.title;
      } else {
        el.financing_plan = { label: "-" };
      }

      return el;
    });
  }, [dataTable?.output]);

  const filteredColumns = useMemo(() => {
    if (columns) {
      if (isPM) {
        return columns?.filter(
          (item: any) => item.accessorKey !== "project_manager",
        );
      } else {
        return columns;
      }
    }
  }, [columns]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      switch (action.type) {
        case "delete": {
          return {
            ...action,
            callBack: (id: string) => {
              setidProjectDeleted(id);
              setOpenModalDelete(true);
            },
          };
        }
        case "button": {
          switch (action.key) {
            case "request-activate":
              return {
                ...action,
                getIcon: (data: any) => <EditNoteIcon />,
                callBack: (row: any) => {
                  setIdProjectRequestActivate(row?.id || null);
                  setOpenModalRequestActivate(true);
                },
              };
            default:
              return action;
          }
        }
        default:
          return action;
      }
    });
  }, [actionForRow, navigate]);

  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    //FUNZIONE PER ABILITAZIONE/ DISABILITAZIONE CAMPI IN BASE A VALORI DELLA ROW
    configRowNew = configRowNew.map((el: ActionsType) => {
      /*If the project has the field deletable true, the elimination is not possibile*/
      if (el.type === "delete" && (!row.original?.deletable || isPM)) {
        return { ...el, disabled: true };
      }

      /*
      if the project is not on state "UNDER_DEVOLPMENT" and the start_date and the end_date are filled, 
      the button to request the activation is not possible
      */
      if (
        el.type === "button" &&
        el.key == "request-activate" &&
        (row.original.status != "UNDER_DEVELOPMENT" ||
          row.original.expected_end_date === null ||
          row.original.expected_end_date === null)
      ) {
        return { ...el, disabled: true };
      }

      return { ...el };
    });

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return actionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "upload": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/download${
                    el ? `/${el}` : ""
                  }?corporate_id=1&type=LEARNER` || "",
                ),
              onUpload: (formData: FormData) => {
                return uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                );
              },
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        case "visibility": {
          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            ...{
              service: `${
                ServicesURL[action.service as KeyURL]
              }?corporate_id=1`,
              types: ["upload", "download", "template"],
              onDownload: (el) =>
                downloadFile(
                  `${ServicesURL[action.service as KeyURL]}/?corporate_id=1` ||
                    "",
                ),
              onUpload: (formData: FormData) =>
                uploadFile(
                  `${ServicesURL[action.service as KeyURL]}?corporate_id=1`,
                  formData,
                ),
            },
          };
          return {
            ...action,
            ...{ callBack: () => setOpenUpload(actionModal) },
          };
        }
        default:
          return action;
      }
    });
  }, [actionsTopToolbar]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-project.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        <Box
          sx={{
            mt: "25px",
          }}
        >
          <Table
            id={PagesID.projects}
            data={dataFormat || []}
            columns={filteredColumns || []}
            configurationTopToolbar={actionsToolbar}
            renderRowActions={customRenderRowActions}
            configurationActionForRow={configurationActionForRow}
            numberOfActions={configurationActionForRow?.length}
            totalPages={dataTable?.metadata?.total_elements}
            modalTitle={t("hero-title")?.replace("-", "") || ""}
          />
        </Box>
      </Container>

      <ModalConfirmsCst
        open={!!openModalDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalDelete(!openModalDelete);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openModalRequestActivate}
        title={t("request-activate")}
        description={t("confirm-request-activate")}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenModalRequestActivate(!openModalRequestActivate);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleRequestActivate()}
        status={openToast}
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
    </AnimationFadeIn>
  );
};

export default Project;
